import { ID, Query, Permission, Role } from "appwrite";
import { databases } from "../appwrite";
import type { Exercise, SetType, Routine, UserProfile } from "../types/db";

// Constants for Database IDs (replace with actual IDs or env vars)
const DATABASE_ID = "697ffbe8000416c190d2"; // Using the ID from book-a-call example
const ROUTINES_TABLE_ID = "routines";

export const adminService = {
  // --- Users ---
  async listUsers(): Promise<UserProfile[]> {
    // Fetches users from the server-side API (which uses Appwrite Users API)
    try {
      const response = await fetch("/api/users");
      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }
      const data = await response.json();
      return (data.users || []) as UserProfile[];
    } catch (error) {
      console.error("Error listing users:", error);
      return [];
    }
  },

  async getUser(userId: string): Promise<UserProfile | null> {
    try {
      const response = await fetch(`/api/users?userId=${userId}`);
      if (!response.ok) {
        throw new Error("Failed to fetch user");
      }
      const user = await response.json();
      return user as UserProfile;
    } catch (error) {
      console.error("Error getting user:", error);
      return {
        $id: userId,
        name: "Unknown User",
        email: "unknown@example.com",
      } as unknown as UserProfile;
    }
  },

  // --- External Data (Public JSONs) ---
  async getExercises(): Promise<Exercise[]> {
    try {
      const response = await fetch("/exercies.json");
      const json = await response.json();
      const exercises = json.data as Exercise[];

      const response2 = await fetch("/exercises_new.json");
      const json2 = await response2.json();
      const exercises2 = json2.data as Exercise[];
      return [...exercises, ...exercises2];
    } catch (error) {
      console.error("Error loading exercises:", error);
      return [];
    }
  },

  async getSetTypes(): Promise<SetType[]> {
    try {
      const response = await fetch("/set_types.json");
      const json = await response.json();
      return json || [];
    } catch (error) {
      console.error("Error loading set types:", error);
      return [];
    }
  },

  // --- Routines ---
  async getUserRoutines(userId: string): Promise<Routine[]> {
    try {
      const response = await databases.listRows({
        databaseId: DATABASE_ID,
        tableId: ROUTINES_TABLE_ID,
        queries: [Query.equal("user_id", userId)],
      });
      return response.rows.map((row) => ({
        ...row,
        exercises:
          typeof row.exercises === "string"
            ? JSON.parse(row.exercises)
            : row.exercises,
      })) as unknown as Routine[];
    } catch (error) {
      console.error("Error getting user routines:", error);
      return [];
    }
  },

  async createRoutine(routine: Omit<Routine, "id">): Promise<Routine | null> {
    try {
      const dataToSave = {
        ...routine,
        exercises: JSON.stringify(routine.exercises),
      };
      const response = await databases.createRow({
        databaseId: DATABASE_ID,
        tableId: ROUTINES_TABLE_ID,
        rowId: ID.unique(),
        data: dataToSave,
      });
      return { ...routine, id: response.$id } as Routine;
    } catch (error) {
      console.error("Error creating routine:", error);
      return null;
    }
  },

  async updateRoutine(
    routineId: string,
    data: Partial<Routine>,
  ): Promise<Routine | null> {
    try {
      const dataToUpdate: any = { ...data };
      if (data.exercises) {
        dataToUpdate.exercises = JSON.stringify(data.exercises);
      }
      const response = await databases.updateRow({
        databaseId: DATABASE_ID,
        tableId: ROUTINES_TABLE_ID,
        rowId: routineId,
        data: dataToUpdate,
      });
      return { ...data, id: response.$id } as Routine;
    } catch (error) {
      console.error("Error updating routine:", error);
      return null;
    }
  },

  async deleteRoutine(routineId: string): Promise<boolean> {
    try {
      await databases.deleteRow({
        databaseId: DATABASE_ID,
        tableId: ROUTINES_TABLE_ID,
        rowId: routineId,
      });
      return true;
    } catch (error) {
      console.error("Error deleting routine:", error);
      return false;
    }
  },
};
